import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import { getUsers, toggleUserActive, getUserById } from "../api/adminApi";
import "./admin-dashboard.css";

// ── User Detail Drawer ────────────────────────────────────────────────────────
function UserDrawer({ userId, onClose, onToggle }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    getUserById(userId)
      .then(res => setData(res))
      .catch(() => alert("Failed to load user details"))
      .finally(() => setLoading(false));
  }, [userId]);

  if (!userId) return null;

  const user             = data?.user;
  const subs             = data?.subscriptions    || [];
  const surveySelections = data?.surveySelections || [];
  const bookings         = data?.bookings         || [];
  const activeSub        = subs.find(s => s.status === "active");

  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";
  const fmtMoney = (n) => n != null ? `₹${Number(n).toLocaleString("en-IN")}` : "—";

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.4)",
        zIndex: 1000, backdropFilter: "blur(2px)"
      }} />

      {/* Drawer */}
      <div style={{
        position: "fixed", top: 0, right: 0, height: "100vh", width: "min(480px, 100vw)",
        background: "#fff", zIndex: 1001, boxShadow: "-8px 0 40px rgba(0,0,0,.15)",
        display: "flex", flexDirection: "column", overflowY: "auto"
      }}>
        {/* Header */}
        <div style={{
          padding: "20px 24px", borderBottom: "1px solid #E2E8F0",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "var(--base-primary)", color: "#fff"
        }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>User Details</div>
            <div style={{ fontSize: 13, opacity: 0.8 }}>Full profile & history</div>
          </div>
          <button onClick={onClose} style={{
            background: "rgba(255,255,255,.2)", border: "none", color: "#fff",
            width: 36, height: 36, borderRadius: "50%", cursor: "pointer",
            fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center"
          }}>✕</button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94A3B8" }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
            Loading user details...
          </div>
        ) : !user ? (
          <div style={{ padding: 40, textAlign: "center", color: "#EF4444" }}>User not found</div>
        ) : (
          <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Avatar + name */}
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{
                width: 64, height: 64, borderRadius: "50%",
                background: "var(--base-primary)", color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 26, fontWeight: 800, flexShrink: 0
              }}>
                {user.full_name?.[0]?.toUpperCase() || "U"}
              </div>
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#1E293B" }}>{user.full_name}</div>
                <div style={{ fontSize: 13, color: "#64748B" }}>ID #{user.id}</div>
                <span style={{
                  display: "inline-block", marginTop: 4,
                  padding: "2px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700,
                  background: user.is_active ? "#DCFCE7" : "#FEE2E2",
                  color: user.is_active ? "#16A34A" : "#DC2626"
                }}>
                  {user.is_active ? "Active" : "Blocked"}
                </span>
              </div>
            </div>

            {/* Contact Info */}
            <Section title="Contact Information">
              <Row label="Phone"      value={user.phone} />
              <Row label="Email"      value={user.email || "Not provided"} />
              <Row label="Joined"     value={fmt(user.created_at)} />
              <Row label="Survey"     value={user.survey_done ? "✅ Completed" : "⏳ Pending"} />
              <Row label="Insurance"  value={user.insurance_applicable ? "✅ Applicable" : "Not applicable"} />
              <Row label="Promotions" value={user.promotions_accepted  ? "✅ Accepted"   : "Not accepted"} />
            </Section>

            {/* Survey Details */}
            {surveySelections.length > 0 && (
              <Section title={`Care Needs Survey (${surveySelections.length} answers)`}>
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {surveySelections.map((sel, i) => (
                    <div key={i} style={{
                      display:"flex", alignItems:"flex-start", gap:12,
                      padding:"8px 10px", borderRadius:10,
                      background: i%2===0?"#F8FAFC":"#fff",
                      border:"1px solid #F1F5F9"
                    }}>
                      <span style={{
                        minWidth:22, height:22, borderRadius:"50%",
                        background:"#1F4E8C", color:"#fff",
                        display:"flex", alignItems:"center", justifyContent:"center",
                        fontSize:11, fontWeight:800, flexShrink:0
                      }}>{sel.step_no}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:"#1E293B", lineHeight:1.4 }}>
                          {sel.label}
                        </div>
                        {sel.value && sel.value !== sel.label && (
                          <div style={{ fontSize:11, color:"#64748B", marginTop:2 }}>{sel.value}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {surveySelections.length === 0 && (
              <Section title="Care Needs Survey">
                <div style={{ color:"#94A3B8", fontSize:13, padding:"6px 0" }}>
                  {user.survey_done ? "Survey completed but no selections recorded." : "⏳ User has not completed the survey yet."}
                </div>
              </Section>
            )}

            {/* Active Subscription */}
            <Section title="Active Subscription">
              {activeSub ? (
                <>
                  <Row label="Plan"    value={activeSub.plan} highlight />
                  <Row label="Amount"  value={fmtMoney(activeSub.amount_paid)} />
                  <Row label="Start"   value={fmt(activeSub.start_date)} />
                  <Row label="Expires" value={fmt(activeSub.end_date)} />
                  <Row label="Status"  value={
                    <span style={{ color: "#16A34A", fontWeight: 700 }}>Active ✓</span>
                  } />
                </>
              ) : (
                <div style={{ color: "#94A3B8", fontSize: 14, padding: "8px 0" }}>
                  No active subscription
                </div>
              )}
            </Section>

            {/* Subscription History */}
            {subs.length > 0 && (
              <Section title={`Subscription History (${subs.length})`}>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {subs.map(s => (
                    <div key={s.id} style={{
                      background: "#F8FAFC", border: "1px solid #E2E8F0",
                      borderRadius: 10, padding: "10px 14px",
                      display: "flex", justifyContent: "space-between", alignItems: "center"
                    }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: "#1E293B" }}>{s.plan}</div>
                        <div style={{ fontSize: 12, color: "#64748B" }}>{fmt(s.start_date)} → {fmt(s.end_date)}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{fmtMoney(s.amount_paid)}</div>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: "2px 8px",
                          borderRadius: 999,
                          background: s.status === "active" ? "#DCFCE7" : s.status === "expired" ? "#FEF3C7" : "#FEE2E2",
                          color: s.status === "active" ? "#16A34A" : s.status === "expired" ? "#D97706" : "#DC2626"
                        }}>
                          {s.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Recent Bookings */}
            {bookings.length > 0 && (
              <Section title={`Recent Bookings (${bookings.length})`}>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {bookings.map(bk => {
                    const STATUS_COLOR = {
                      awaiting_confirmation:"#1E40AF", confirmed:"#065F46",
                      completed:"#14532D", cancelled:"#6B7280",
                      in_progress:"#3730A3", pending_payment:"#92400E"
                    };
                    const slotLabel = {
                      early:"Before 08 AM", morning:"08–12 PM",
                      afternoon:"12–04 PM", evening:"After 04 PM"
                    };
                    return (
                      <div key={bk.id} style={{
                        background:"#F8FAFC", border:"1px solid #E2E8F0",
                        borderRadius:10, padding:"10px 12px"
                      }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4 }}>
                          <div style={{ fontWeight:700, fontSize:13, color:"#1E293B" }}>
                            {bk.service}
                          </div>
                          <span style={{
                            fontSize:10, fontWeight:800, padding:"2px 8px",
                            borderRadius:999, background:"#EFF6FF",
                            color: STATUS_COLOR[bk.status]||"#64748B"
                          }}>
                            {bk.status?.replace(/_/g," ").toUpperCase()}
                          </span>
                        </div>
                        <div style={{ fontSize:12, color:"#64748B" }}>
                          📅 {new Date(bk.booking_date).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})}
                          {" · "}{slotLabel[bk.time_slot]||bk.time_slot}
                        </div>
                        <div style={{ fontSize:12, color:"#1F4E8C", fontWeight:700, marginTop:2 }}>
                          ₹{Number(bk.price).toLocaleString("en-IN")}
                          {" · "}<span style={{ color: bk.payment_status==="paid"?"#16A34A":"#D97706" }}>
                            {bk.payment_status==="paid"?"✓ Paid":bk.payment_status||"Pending"}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Section>
            )}

            {/* Action */}
            <button
              onClick={() => onToggle(user.id, user.is_active)}
              style={{
                padding: "12px 20px", borderRadius: 12, cursor: "pointer",
                border: "none", fontWeight: 700, fontSize: 14,
                background: user.is_active ? "#FEE2E2" : "#DCFCE7",
                color: user.is_active ? "#DC2626" : "#16A34A"
              }}
            >
              {user.is_active ? "🚫 Block this user" : "✅ Unblock this user"}
            </button>

          </div>
        )}
      </div>
    </>
  );
}

// ── Small helpers ─────────────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 14, padding: "16px", overflow: "hidden" }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: "#1F4E8C", textTransform: "uppercase",
        letterSpacing: ".6px", marginBottom: 14, paddingBottom: 8, borderBottom: "1px solid #E2E8F0" }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "flex-start",
      gap: 12, padding: "8px 0", borderBottom: "1px solid #E2E8F0", fontSize: 14
    }}>
      <span style={{ color: "#94A3B8", fontWeight: 700, fontSize: 12,
        textTransform: "uppercase", letterSpacing: ".3px", flexShrink: 0, paddingTop: 1 }}>
        {label}
      </span>
      <span style={{
        fontWeight: highlight ? 800 : 600,
        color: highlight ? "var(--base-primary)" : "#1E293B",
        textAlign: "right", wordBreak: "break-word"
      }}>{value}</span>
    </div>
  );
}

// ── Main Users Page ───────────────────────────────────────────────────────────
export default function Users() {
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [page, setPage]           = useState(1);
  const [pagination, setPagination] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const fetchUsers = async (p = 1, s = "") => {
    try {
      setLoading(true);
      const data = await getUsers({ page: p, limit: 20, search: s });
      setUsers(data.users);
      setPagination(data.pagination);
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(page, search); }, [page]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers(1, search);
  };

  const handleToggle = async (id, currentStatus) => {
    const action = currentStatus ? "block" : "unblock";
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;
    try {
      await toggleUserActive(id);
      setSelectedId(null);
      fetchUsers(page, search);
    } catch (err) {
      alert(err?.response?.data?.message || "Action failed");
    }
  };

  return (
    <div className="adm-layout">
      <Sidebar />
      <main className="adm-main">
        <div className="adm-shell">
          <Topbar title="Users" subtitle="Click any row to view full details" />

          {/* Search */}
          <form onSubmit={handleSearch} style={{ marginBottom: 16, display: "flex", gap: 8 }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or phone..." style={{ flex:1, minWidth:160 }}
              style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #ddd", flex: 1 }}
            />
            <button type="submit" style={{
              padding: "8px 16px", borderRadius: 8,
              background: "var(--base-primary)", color: "#fff", border: "none", cursor: "pointer", fontWeight: 700
            }}>
              Search
            </button>
          </form>

          <div className="adm-panel" style={{ gridColumn: "span 12" }}>
            <h3>All Users {pagination && `(${pagination.total} total)`}</h3>

            {loading ? (
              <div style={{ padding: 10, color: "var(--muted-neutral)" }}>Loading...</div>
            ) : (
              <>
                {/* ── Desktop table ── */}
                <div className="usr-table-wrap">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>ID</th><th>Name</th><th>Phone</th><th>Plan</th><th>Joined</th><th>Survey</th><th>Insurance</th><th>Status</th><th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} onClick={() => setSelectedId(u.id)}
                        style={{ cursor:"pointer" }}
                        onMouseEnter={e=>e.currentTarget.style.background="#F1F5F9"}
                        onMouseLeave={e=>e.currentTarget.style.background=""}>
                        <td>{u.id}</td>
                        <td style={{ fontWeight:600 }}>{u.full_name}</td>
                        <td>{u.phone}</td>
                        <td>{u.plan_name||<span style={{ color:"#94A3B8" }}>No Plan</span>}</td>
                        <td>{new Date(u.created_at).toLocaleDateString("en-IN")}</td>
                        <td>{u.survey_done?<span style={{color:"#16A34A",fontWeight:700}}>✅ Done</span>:<span style={{color:"#D97706",fontWeight:700}}>⏳ Pending</span>}</td>
                        <td>{u.insurance_applicable?<span style={{color:"#0369A1",fontWeight:700}}>✅ Yes</span>:<span style={{color:"#94A3B8"}}>—</span>}</td>
                        <td>{u.is_active?<span className="adm-badge adm-badge-success">Active</span>:<span className="adm-badge adm-badge-danger">Blocked</span>}</td>
                        <td onClick={e=>e.stopPropagation()}>
                          <button onClick={()=>handleToggle(u.id,u.is_active)} style={{
                            padding:"4px 10px",borderRadius:6,cursor:"pointer",
                            background:u.is_active?"#FEE2E2":"#DCFCE7",
                            color:u.is_active?"#DC2626":"#16A34A",
                            border:"none",fontWeight:700}}>
                            {u.is_active?"Block":"Unblock"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>

                {/* ── Mobile flow list ── */}
                <div className="usr-cards-wrap">
                  {users.map(u => {
                    const joinedDate = new Date(u.created_at).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"});
                    const barColor = u.is_active ? "#1F4E8C" : "#DC2626";
                    return (
                      <div key={u.id} onClick={()=>setSelectedId(u.id)}
                        className="mb-card" style={{ marginBottom:12, cursor:"pointer" }}>

                        {/* Coloured top bar */}
                        <div className="mb-card-bar" style={{ background: barColor }}/>

                        {/* Title row */}
                        <div className="mb-card-top">
                          <div>
                            <div className="mb-bk-id">User #{u.id}</div>
                            <div className="mb-svc-name">{u.full_name}</div>
                            <div className="mb-svc-cat">📱 {u.phone}</div>
                          </div>
                          <div className="mb-card-right">
                            <span className="mb-status-badge" style={{
                              background: u.is_active?"#D1FAE5":"#FEE2E2",
                              color: u.is_active?"#065F46":"#DC2626"
                            }}>
                              {u.is_active?"● Active":"● Blocked"}
                            </span>
                          </div>
                        </div>

                        {/* Info grid */}
                        <div className="mb-info-row">
                          <div className="mb-info-item">
                            <span className="mb-info-icon">💳</span>
                            <div>
                              <div className="mb-info-label">Plan</div>
                              <div className="mb-info-val" style={{ color: u.plan_name?"#1F4E8C":"#94A3B8" }}>
                                {u.plan_name||"No Plan"}
                              </div>
                            </div>
                          </div>
                          <div className="mb-info-item">
                            <span className="mb-info-icon">📅</span>
                            <div>
                              <div className="mb-info-label">Joined</div>
                              <div className="mb-info-val">{joinedDate}</div>
                            </div>
                          </div>
                          <div className="mb-info-item">
                            <span className="mb-info-icon">📋</span>
                            <div>
                              <div className="mb-info-label">Survey</div>
                              <div className="mb-info-val" style={{ color: u.survey_done?"#16A34A":"#D97706" }}>
                                {u.survey_done?"✅ Done":"⏳ Pending"}
                              </div>
                            </div>
                          </div>
                          <div className="mb-info-item">
                            <span className="mb-info-icon">🛡️</span>
                            <div>
                              <div className="mb-info-label">Insurance</div>
                              <div className="mb-info-val" style={{ color: u.insurance_applicable?"#0369A1":"#94A3B8" }}>
                                {u.insurance_applicable?"✅ Yes":"—"}
                              </div>
                            </div>
                          </div>
                          <div className="mb-info-item">
                            <span className="mb-info-icon">🔒</span>
                            <div>
                              <div className="mb-info-label">Status</div>
                              <div className="mb-info-val" style={{ color: u.is_active?"#16A34A":"#DC2626" }}>
                                {u.is_active?"Active":"Blocked"}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Footer action */}
                        <div className="mb-card-footer">
                          <div className="mb-booked-on">Tap to view full profile</div>
                          <button onClick={e=>{e.stopPropagation();handleToggle(u.id,u.is_active);}} style={{
                            padding:"7px 16px", borderRadius:20, border:"none",
                            background: u.is_active?"#FEE2E2":"#D1FAE5",
                            color: u.is_active?"#DC2626":"#16A34A",
                            fontWeight:800, fontSize:12, cursor:"pointer", fontFamily:"inherit"
                          }}>
                            {u.is_active?"Block":"Unblock"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {pagination && pagination.totalPages > 1 && (
                  <div style={{ marginTop: 16, display: "flex", gap: 8, alignItems: "center" }}>
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                      style={{ padding: "6px 12px", borderRadius: 6, cursor: "pointer", border: "1px solid #ddd" }}>
                      ← Prev
                    </button>
                    <span>Page {page} of {pagination.totalPages}</span>
                    <button onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))} disabled={page === pagination.totalPages}
                      style={{ padding: "6px 12px", borderRadius: 6, cursor: "pointer", border: "1px solid #ddd" }}>
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* User Detail Drawer */}
      <UserDrawer
        userId={selectedId}
        onClose={() => setSelectedId(null)}
        onToggle={handleToggle}
      />
    </div>
  );
}